import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  styled, Grid,
} from "@mui/material";
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import React, { FC, useContext, useState } from "react";
import {
  updateUser,
  useAuth0Token,
} from "../common/http-requests";
import { updateUserRequestType } from "../common/type";
import { checkValidUserId } from "../common/util";
import { CircularProgressMolecule } from "../components/molecules/CircularProgressMolecule";
import { FeedbackBar } from "../components/molecules/FeedbackBar";
import { Store } from "../store";
import types from "../store/types";
import { VisualizedMenus } from "../components/VisualizedMenus";
import { Toolbar } from "../components/FooterToolbar";
import { useTranslation } from 'react-i18next';
import { useCustomMediaQuery } from "../common/useCustomMediaQuery";
import { pageMt } from "../common/util";
import { basicColor, themeColors } from "../common/colors";
import {
  useIsReadOnly,
  useUserNickname,
  useUserStudentNumber,
} from "../hooks/useStoreGetters";

const LINE_CHART_SIZE: number[] = [
  800, 320
];

const Title = styled(Box)(() => ({
  position: "relative",
  width: '100%',
  height: 'auto',
  maxWidth: `${LINE_CHART_SIZE[0]}px`,
  margin: 'auto',
  borderBottom: `solid 1px ${themeColors[0]}`,
  color: `${themeColors[0]}`,
  textAlign: 'left',
  marginBottom: '2rem',
}));

const StyledLabel = styled('label')({
  position: 'relative',
  width: '100%',
  color: `${themeColors[0]}`,
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: `${themeColors[0]}`,
  },
});

const StyledTypography = styled(Typography)({
  position: 'relative',
  width: '100%',
  color: `${themeColors[0]}`,
  lineHeight: '1.6rem',
  minWidth: '2em',
  minHeight: '1.6rem',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: `${themeColors[0]}`,
  },
});

const StyledTextField = styled(TextField)({
  position: 'relative',
  width: '100%',
  border: 'none',
  background: 'none',
  paddingRight: '4rem',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '4px',
    left: 0,
    width: '100%',
    height: '2px',
    backgroundColor: `${themeColors[0]}`,
  },
});

const MaxTextLength_UserName = 100;
// const SecretPasswordText = "********";

const AccountPage: FC = () => {
  const { t } = useTranslation();
  const { isDesktop } = useCustomMediaQuery();
  const { getToken } = useAuth0Token();
  const { state, dispatch } = useContext(Store);
  const isReadOnly = useIsReadOnly();
  const [isSending, setIsSending] = useState(false);

  // エラーメッセージ表示
  const [feedbackInfo, setFeedbackInfo] = useState({
    open: false,
    text: "",
    type: "success" as "error" | "success" | "warning" | "info",
    autoHideDuration: null,
  });

  const handleFeedbackClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedbackInfo({ ...feedbackInfo, open: false });
  };

  // 学籍番号８桁、編集不可
  const userStudentNumber = useUserStudentNumber();

  // パスワード（編集不可）
  // TODO: ログインパスワードを「＊（非表示）」と表示切替できること
  // const [userPassword, setUserPassword] = useState<string>("");

  const initUserNickname = useUserNickname();
  const [userNickname, setNickname] = useState<string>(initUserNickname);

  // ユーザ情報を更新
  const saveUserData = async () => {
    setIsSending(true);
    try {
      // ユーザIDの妥当性チェック
      if (! await checkValidUserId(state.userId, state.userEmail, getToken, setFeedbackInfo, feedbackInfo)) {
        return;
      }
      // 画面データの妥当性チェック
      if (! await checkValidUserData()) {
        return;
      }

      const requestParams: updateUserRequestType = {
        login_user_id: state.userId,
        nickname: userNickname,
      };
      await updateUser(await getToken(), requestParams);

      dispatch({
        type: types.SET_USER_INFO,
        payload: {
          userId: state.userId,
          userEmail: state.userEmail,
          userName: state.userName,
          userNickname,
          userType: state.userType,
          userStudentNumber: state.userStudentNumber,
        },
      });
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: t("msg.saved"),
        type: "success",
      });
    } catch {
    } finally {
      setIsSending(false);
    }
  };

  const checkValidUserData = async () => {
    // ニックネーム テキストフィールド
    if ((userNickname === "") || (userNickname.length > MaxTextLength_UserName)) {
      setFeedbackInfo({
        ...feedbackInfo,
        open: true,
        text: "ニックネームは100文字以内で入力してください。",
        type: "error",
      });
      return false;
    }

    return true;
  };

  // 保存ボタン
  const handleClickSaveButton = async () => {
    if (isReadOnly) return;
    await saveUserData();
  };

  return (
    <Container maxWidth="xl">
      <VisualizedMenus />
      <FeedbackBar
        feedbackInfo={feedbackInfo}
        handleClose={handleFeedbackClose}
      ></FeedbackBar>

      {isSending ?
        <CircularProgressMolecule displayLabel={t("msg.loading")} /> :
        (
          <Box mt={pageMt(isDesktop)} className="mb-2 p-2 w-[80%] mx-auto" maxWidth={`${LINE_CHART_SIZE[0]}px`}>
            <Title>
              <Typography
                variant="h5"
                style={{ marginTop: 10, textAlign: "left" }}
              >
                <PersonOutlineOutlinedIcon sx={{ margin: '.5rem .8rem' }} />{t("menu.account")}
              </Typography>
            </Title>
            <FieldComponent label={t("account.number")} value={userStudentNumber} />
            {isReadOnly ?
              <FieldComponent label={t("account.nickname")} value={userNickname} /> :
              // ニックネーム（読み取り専用モードでない場合は、編集可）
              <FieldComponent label={t("account.nickname")} value={userNickname} onChange={(e) => setNickname(e.target.value)} />
            }
            <Box
              sx={{
                width: '20%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: 'fit-content',
                margin: '2rem auto',
              }}
            >
              <Button
                style={{
                  width: '100px',
                  marginLeft: '20px',
                  fontSize: '16px',
                  alignItems: 'center',
                  backgroundColor: basicColor,
                }}
                variant="contained"
                disabled={isReadOnly}
                onClick={() => handleClickSaveButton()}
              >
                {t("button.save")}
              </Button>
            </Box>
          </Box>
        )}
      <Toolbar />
    </Container >
  );
};

export default AccountPage;

interface FieldComponentProps {
  label: string;
  value: string;
  onChange?: (event: any) => void;
}

const FieldComponent: React.FC<FieldComponentProps> = ({
  label,
  value,
  onChange
}) => (

  <Grid container spacing={2} my={2}>
    <Grid item xs={12}>
      <Grid container alignItems="center">
        <Grid item xs={4}><Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <StyledLabel
          >
            {label}
          </StyledLabel></Box>
        </Grid>
        <Grid item xs={0.3}></Grid>
        <Grid item xs={7.7} position={'relative'}>
          {onChange ? (<StyledTextField
            variant="standard"
            value={value}
            fullWidth
            InputProps={{ disableUnderline: true }}
            onChange={(e) => onChange(e)}
          />) : (
            <StyledTypography>{value}</StyledTypography>
          )}
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);